// Grouped, inset lists, as iOS Settings has them: a section's heading above, rows with a label and what it's set to,
// and a note below. A row goes somewhere (a chevron), does something, shows a value, holds a switch or a field, or is
// one choice of several.
import {
  Children,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import { cx } from './cx';
import { CaretRightIcon, CaretUpDownIcon, CheckIcon } from './icons';
import s from './List.module.css';

export function ListSection({
  header,
  headerAction,
  footer,
  level = 2,
  children,
  className,
}: {
  /** The section's heading, read as one. */
  header?: ReactNode;
  /** A small action beside the heading: "Edit", "See all". */
  headerAction?: ReactNode;
  footer?: ReactNode;
  level?: 2 | 3;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const Heading = level === 2 ? 'h2' : 'h3';
  // Each row is an item in the list, so it's read as one of so many.
  const rows = Children.toArray(children).filter(isValidElement) as ReactElement<{ icon?: ReactNode }>[];
  return (
    <section className={cx(s.section, className)} aria-labelledby={header ? id : undefined}>
      {header && (
        <div className={s.header}>
          <Heading id={id}>{header}</Heading>
          {headerAction}
        </div>
      )}
      <ul className={s.list} role="list">
        {rows.map((row, i) => (
          <li key={row.key ?? i} className={cx(s.item, row.props.icon !== undefined && s.hasIcon)}>
            {row}
          </li>
        ))}
      </ul>
      {footer && <div className={s.footer}>{footer}</div>}
    </section>
  );
}

interface RowLook {
  label: ReactNode;
  /** A second line, smaller. */
  subtitle?: ReactNode;
  /** What it's set to, at the end. */
  detail?: ReactNode;
  icon?: ReactNode;
  tone?: 'normal' | 'accent' | 'destructive';
}

function RowIcon({ icon }: { icon?: ReactNode }) {
  return icon ? (
    <span className={s.rowIcon} aria-hidden="true">
      {icon}
    </span>
  ) : null;
}

function RowInside({ label, subtitle, detail, icon, accessory }: RowLook & { accessory?: ReactNode }) {
  return (
    <>
      <RowIcon icon={icon} />
      <span className={s.text}>
        <span className={s.label}>{label}</span>
        {subtitle && <span className={s.subtitle}>{subtitle}</span>}
      </span>
      {detail !== undefined && <span className={s.detail}>{detail}</span>}
      {accessory && <span className={s.accessory}>{accessory}</span>}
    </>
  );
}

const chevron = <CaretRightIcon size={16} weight="bold" aria-hidden="true" />;

/** A row that goes somewhere: a chevron at its end. */
export function ListLink({ to, tone = 'normal', state, ...look }: RowLook & { to: string; state?: unknown }) {
  return (
    <Link to={to} state={state} className={cx(s.row, tone !== 'normal' && s[tone])}>
      <RowInside {...look} accessory={chevron} />
    </Link>
  );
}

/** A row that does something. `current` marks the one being shown beside the list, as a sidebar marks its place. */
export function ListButton({
  onClick,
  tone = 'normal',
  disabled = false,
  current = false,
  accessory,
  ...look
}: RowLook & { onClick: () => void; disabled?: boolean; current?: boolean; accessory?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-current={current ? 'true' : undefined}
      className={cx(s.row, tone !== 'normal' && s[tone], disabled && s.disabled, current && s.current)}
    >
      <RowInside {...look} accessory={accessory} />
    </button>
  );
}

/** A row that shows something: a label and its value. */
export function ListValue(look: RowLook) {
  return (
    <div className={s.row}>
      <RowInside {...look} />
    </div>
  );
}

/** A row with a control at its end, such as a switch: the whole row names it, and pressing it presses the control. */
export function ListControl({ control, ...look }: RowLook & { control: ReactNode }) {
  return (
    <label className={s.row}>
      <RowInside {...look} accessory={control} />
    </label>
  );
}

/** A row with a field at its end: "Name … Sam". */
export function ListField({
  label,
  icon,
  suffix,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'children'> & {
  label: ReactNode;
  icon?: ReactNode;
  suffix?: ReactNode;
}) {
  const id = useId();
  return (
    <div className={cx(s.row, s.fieldRow)}>
      <RowIcon icon={icon} />
      <label htmlFor={input.id ?? id} className={s.label}>
        {label}
      </label>
      <input id={input.id ?? id} {...input} />
      {suffix && <span className={s.suffix}>{suffix}</span>}
    </div>
  );
}

/** A row with a choice of several at its end, as the browser offers it. */
export function ListSelect({
  label,
  icon,
  children,
  ...select
}: SelectHTMLAttributes<HTMLSelectElement> & { label: ReactNode; icon?: ReactNode }) {
  const id = useId();
  return (
    <div className={cx(s.row, s.fieldRow)}>
      <RowIcon icon={icon} />
      <label htmlFor={select.id ?? id} className={s.label}>
        {label}
      </label>
      <select id={select.id ?? id} {...select}>
        {children}
      </select>
      <CaretUpDownIcon className={s.selectCaret} size={16} weight="bold" aria-hidden="true" />
    </div>
  );
}

/**
 * One choice of several (a radio, `type="radio"` with a shared `name`), or one of many to tick (`type="checkbox"`):
 * a checkmark shows what's chosen.
 */
export function ListChoice({
  label,
  subtitle,
  icon,
  type = 'radio',
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  type?: 'radio' | 'checkbox';
}) {
  return (
    <label className={cx(s.row, s.choice)}>
      <input type={type} {...input} />
      <RowIcon icon={icon} />
      <span className={s.text}>
        <span className={s.label}>{label}</span>
        {subtitle && <span className={s.subtitle}>{subtitle}</span>}
      </span>
      {type === 'radio' ? (
        <CheckIcon className={s.check} size={20} weight="bold" aria-hidden="true" />
      ) : (
        <span className={s.box} aria-hidden="true">
          <CheckIcon size={14} weight="bold" />
        </span>
      )}
    </label>
  );
}
